/**
 * AccountantWorkspace.jsx — ACCOUNTANT-WORKSPACE-001
 * =====================================================
 *
 * Dense, grid-first JWT-scoped Accountant Workspace for the
 * `provider_accountant` role (read-only supervisory for
 * `provider_admin`). Replaces the legacy card-based
 * `AccountingDashboard.jsx` at the same routes.
 *
 * Visual authority: COORDINATOR-GRID-UX-001. Reuses the same
 * `journey-ux` primitives (`DataGrid`, `DetailDrawer`, `FilterChips`,
 * `Pill`) shipped in the Coordinator / Doctor / After-Sales
 * workspaces. NO cards, NO dashboard tiles.
 *
 * Data: GET /api/clinic/accountant/{summary, receivables, payables,
 * commissions, refunds, subscriptions, contracts, history, statement}
 *
 * Every endpoint is JWT clinic-scoped — query/path clinic ids are
 * IGNORED on the backend (see DATA_ISOLATION_VALIDATION.md).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calculator, RefreshCw, ExternalLink } from 'lucide-react';

import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import {
  DataGrid,
  DetailDrawer,
  DrawerField,
  FilterChips,
  Pill,
} from '../../components/journey-ux';
import ProviderFinance from './ProviderFinance';
import AccountingGradeReports from '../../components/AccountingGradeReports';

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */
const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
  } catch { return '—'; }
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return '—'; }
};
const fmtMoney = (n, cur = 'EUR') => {
  if (n == null) return '—';
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(n);
  } catch { return `${(n).toFixed(2)} ${cur}`; }
};
const STATUS_TONE = {
  PAID: 'emerald', NOT_PAID: 'amber', REFUNDED: 'rose',
  paid: 'emerald', failed: 'rose', processing: 'sky', requested: 'amber',
  approved: 'sky', completed: 'emerald', trialing: 'sky', active: 'emerald',
  pending: 'amber', frozen: 'emerald',
};

const VIEWS = [
  // UI-B · Canonical Provider Finance surface (statement + booking drill-down).
  { id: 'finance',        label: 'Finance',       path: null /* dedicated composed view */ },
  { id: 'receivables',    label: 'A/R',           path: 'receivables' },
  { id: 'payables',       label: 'A/P',           path: 'payables' },
  { id: 'commissions',    label: 'Commissions',   path: 'commissions' },
  { id: 'refunds',        label: 'Refunds',       path: 'refunds' },
  { id: 'subscriptions',  label: 'Subscriptions', path: 'subscriptions' },
  { id: 'contracts',      label: 'Contracts',     path: 'contracts' },
  { id: 'history',        label: 'History',       path: 'history' },
  // COMMISSION-STATEMENT-UI-01: platform commission statements — NOT a
  // statutory tax invoice / e-Fatura. Consumes existing PG invoice_documents
  // via the accountant workspace tenant-scoped read.
  { id: 'statements',     label: 'Commission Statements', path: 'commission-statements' },
  // FINANCIAL-REPORTING-REMEDIATION · accounting-grade reports (GL, trial
  // balance, AR/AP ageing, deferred revenue, VAT, PSP settlement) read from
  // the SAME canonical /api/accounting authority, server-scoped to this clinic.
  { id: 'reports',        label: 'Accounting Reports', path: null },
];

const BUCKETS = [
  { id: 'all',         label: 'All' },
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'paid',        label: 'Paid' },
  { id: 'overdue',     label: 'Overdue' },
  { id: 'refunded',    label: 'Refunded' },
];

/* -------------------------------------------------------------------- */
/*  Column factories                                                    */
/* -------------------------------------------------------------------- */
const _statusLabel = (t, code) => {
  if (!code) return '—';
  return t(`common.status.${code}`, { defaultValue: code });
};

const receivablesColumns = (t) => [
  { id: 'order', header: 'Order',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.order_id || '').slice(-8)}</span>,
    width: 'w-28' },
  { id: 'patient', header: 'Patient',
    render: (r) => (
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{r.country || '—'}</span>
        <span className="text-slate-800 truncate">{r.patient_name || r.patient_id || '—'}</span>
      </div>
    ) },
  { id: 'booking', header: 'Booking',
    render: (r) => r.booking_id
      ? <span className="font-mono text-[10.5px] text-slate-600">{r.booking_id.slice(0, 12)}</span>
      : r.case_id ? <span className="font-mono text-[10.5px] text-slate-500">case:{r.case_id.slice(-6)}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'treatment', header: 'Treatment',
    render: (r) => <span className="text-slate-700 truncate">{r.treatment_name || '—'}</span> },
  { id: 'amount', header: 'Amount', align: 'right',
    render: (r) => <span className="font-semibold text-slate-800">{fmtMoney(r.amount, r.currency)}</span> },
  { id: 'status', header: 'Status',
    render: (r) => (
      <Pill tone={STATUS_TONE[r.status] || (r.is_overdue ? 'rose' : 'slate')}>
        {r.is_overdue ? t('common.status.OVERDUE', { defaultValue: 'Overdue' }) : _statusLabel(t, r.status)}
      </Pill>
    ) },
  { id: 'created', header: 'Created',
    render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
  { id: 'due', header: 'Due',
    render: (r) => r.due_date
      ? <span className={r.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}>{fmtDate(r.due_date)}</span>
      : <span className="text-slate-300">—</span> },
];

const payablesColumns = (t) => [
  { id: 'record', header: 'Record',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{(r.record_id || '').slice(0, 12)}</span>,
    width: 'w-32' },
  { id: 'obligation', header: 'Obligation',
    render: (r) => <Pill tone="sky">{r.obligation_type || 'commission_payable'}</Pill> },
  { id: 'gross', header: 'Gross', align: 'right',
    render: (r) => <span className="text-slate-700">{fmtMoney(r.gross, r.currency)}</span> },
  { id: 'commission', header: 'Commission', align: 'right',
    render: (r) => <span className="text-amber-700">{fmtMoney(r.commission, r.currency)}</span> },
  { id: 'net', header: 'Net', align: 'right',
    render: (r) => <span className="font-semibold text-emerald-700">{fmtMoney(r.net_amount, r.currency)}</span> },
  { id: 'reserve', header: 'Reserve', align: 'right',
    render: (r) => r.reserve > 0
      ? <span className="text-rose-700">{fmtMoney(r.reserve, r.currency)}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'payout_status', header: 'Payout',
    render: (r) => <Pill tone={STATUS_TONE[r.payout_status] || 'amber'}>{_statusLabel(t, r.payout_status || 'pending')}</Pill> },
  { id: 'state', header: 'State',
    render: (r) => <Pill tone={STATUS_TONE[r.state] || 'slate'}>{_statusLabel(t, r.state)}</Pill> },
  { id: 'created', header: 'Date',
    render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
];

const commissionsColumns = (t) => [
  { id: 'record', header: 'Record',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{(r.record_id || '').slice(0, 12)}</span>,
    width: 'w-32' },
  { id: 'order', header: 'Order',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{(r.order_id || '—').slice(-8)}</span> },
  { id: 'sale_class', header: 'Sale class',
    render: (r) => <Pill tone="sky">{r.sale_class || '—'}</Pill> },
  { id: 'gross', header: 'Gross', align: 'right',
    render: (r) => <span className="text-slate-700">{fmtMoney(r.gross, r.currency)}</span> },
  { id: 'commission', header: 'Commission', align: 'right',
    render: (r) => <span className="text-amber-700">{fmtMoney(r.commission, r.currency)}</span> },
  { id: 'net', header: 'Net to provider', align: 'right',
    render: (r) => <span className="font-semibold text-emerald-700">{fmtMoney(r.net, r.currency)}</span> },
  { id: 'rate', header: 'Rate', align: 'right',
    render: (r) => r.rate_bps_at_freeze != null
      ? <span className="font-mono text-[11px] text-slate-600">{(r.rate_bps_at_freeze / 100).toFixed(2)}%</span>
      : <span className="text-slate-300">—</span> },
  { id: 'state', header: 'State',
    render: (r) => <Pill tone={STATUS_TONE[r.state] || 'slate'}>{_statusLabel(t, r.state)}</Pill> },
  { id: 'lineage', header: 'Lineage',
    render: (r) => Array.isArray(r.lineage_chain) && r.lineage_chain.length
      ? <Pill tone="violet">{r.lineage_chain.length} step</Pill>
      : <span className="text-slate-300">—</span> },
  { id: 'created', header: 'Frozen',
    render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
];

const refundsColumns = (t) => [
  { id: 'refund', header: 'Refund',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{(r.refund_id || '').slice(0, 12)}</span>,
    width: 'w-32' },
  { id: 'order', header: 'Order',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{(r.order_id || '—').slice(-8)}</span> },
  { id: 'amount', header: 'Amount', align: 'right',
    render: (r) => <span className="font-semibold text-slate-800">{fmtMoney(r.amount, r.currency)}</span> },
  { id: 'type', header: 'Type',
    render: (r) => r.refund_type ? <Pill tone="sky">{r.refund_type}</Pill> : <span className="text-slate-300">—</span> },
  { id: 'reason', header: 'Reason',
    render: (r) => <span className="text-slate-700 truncate">{r.reason_code || r.reason || '—'}</span> },
  { id: 'status', header: 'Status',
    render: (r) => <Pill tone={STATUS_TONE[r.status] || 'slate'}>{_statusLabel(t, r.status)}</Pill> },
  { id: 'created', header: 'Created',
    render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
  { id: 'completed', header: 'Completed',
    render: (r) => r.completed_at ? <span className="text-slate-500">{fmtRel(r.completed_at)}</span> : <span className="text-slate-300">—</span> },
];

const subscriptionsColumns = (t) => [
  { id: 'txn', header: 'Txn',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{(r.transaction_id || '').slice(0, 14)}</span>,
    width: 'w-36' },
  { id: 'plan', header: 'Plan',
    render: (r) => <Pill tone="violet">{r.package_key || '—'}</Pill> },
  { id: 'amount', header: 'Amount', align: 'right',
    render: (r) => <span className="text-slate-700">{fmtMoney(r.amount, (r.currency || 'EUR').toUpperCase())}</span> },
  { id: 'trial', header: 'Trial',
    render: (r) => r.trial ? <Pill tone="sky">Trial</Pill> : <span className="text-slate-300">—</span> },
  { id: 'status', header: 'Status',
    render: (r) => <Pill tone={STATUS_TONE[r.status] || 'slate'}>{_statusLabel(t, r.status)}</Pill> },
  { id: 'started', header: 'Started',
    render: (r) => <span className="text-slate-500">{fmtRel(r.started_at)}</span> },
  { id: 'renewal', header: 'Renewal',
    render: (r) => r.renewal_at ? <span className="text-slate-600">{fmtDate(r.renewal_at)}</span> : <span className="text-slate-300">—</span> },
];

const contractsColumns = (t) => [
  { id: 'contract', header: 'Contract',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{(r.contract_id || '').slice(0, 14)}</span>,
    width: 'w-36' },
  { id: 'offer', header: 'Offer',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{(r.offer_id || '—').slice(-8)}</span> },
  { id: 'type', header: 'Type',
    render: (r) => <Pill tone="sky">{r.commission_type || '—'}</Pill> },
  { id: 'rate', header: 'Rate', align: 'right',
    render: (r) => r.effective_rate_bps != null
      ? <span className="font-mono text-[11px] text-slate-600">{(r.effective_rate_bps / 100).toFixed(2)}%</span>
      : <span className="text-slate-300">—</span> },
  { id: 'commission_amount', header: 'Commission', align: 'right',
    render: (r) => <span className="text-amber-700">{fmtMoney(r.commission_amount, r.currency)}</span> },
  { id: 'net', header: 'Net', align: 'right',
    render: (r) => <span className="font-semibold text-emerald-700">{fmtMoney(r.net_transfer_to_provider, r.currency)}</span> },
  { id: 'campaign', header: 'Campaign',
    render: (r) => r.campaign_id ? <Pill tone="violet">{r.campaign_id.slice(0, 10)}</Pill> : <span className="text-slate-300">—</span> },
  { id: 'rule', header: 'Resolved by',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-500">{(r.resolved_by_rule || '—').slice(0, 16)}</span> },
  { id: 'status', header: 'Status',
    render: (r) => <Pill tone={STATUS_TONE[r.status] || 'slate'}>{_statusLabel(t, r.status)}</Pill> },
  { id: 'effective', header: 'Effective',
    render: (r) => <span className="text-slate-500">{fmtRel(r.effective_at)}</span> },
];

const historyColumns = (t) => [
  { id: 'date', header: 'Date',
    render: (r) => <span className="text-slate-500">{fmtDate(r.date || r.created_at)}</span>,
    width: 'w-24' },
  { id: 'movement', header: 'Movement',
    render: (r) => <Pill tone={STATUS_TONE[r.status] || 'sky'}>{(r.movement_type || '—').replace(/_/g, ' ')}</Pill> },
  { id: 'reference', header: 'Reference',
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{r.reference || '—'}</span> },
  { id: 'cp', header: 'Counterparty',
    render: (r) => <span className="text-slate-600">{r.counterparty_type || '—'}</span> },
  { id: 'gross', header: 'Gross', align: 'right',
    render: (r) => <span className="text-slate-700">{fmtMoney(r.gross_amount, r.currency)}</span> },
  { id: 'debit', header: 'Debit', align: 'right',
    render: (r) => (r.debit ? <span className="text-rose-700">{fmtMoney(r.debit, r.currency)}</span> : <span className="text-slate-300">—</span>) },
  { id: 'credit', header: 'Credit', align: 'right',
    render: (r) => (r.credit ? <span className="text-emerald-700">{fmtMoney(r.credit, r.currency)}</span> : <span className="text-slate-300">—</span>) },
  { id: 'status', header: 'Status',
    render: (r) => <Pill tone={STATUS_TONE[r.status] || 'slate'}>{_statusLabel(t, r.status)}</Pill> },
  { id: 'notes', header: 'Notes',
    render: (r) => <span className="text-slate-500 truncate" title={r.notes}>{r.notes || '—'}</span> },
];

// COMMISSION-STATEMENT-UI-01: Platform Commission Statements grid.
// These are the platform's commercial commission statements — they are
// NOT statutory tax invoices / e-Fatura. Label is enforced in the UI so
// the domain semantics don't drift.
const STATEMENT_KIND_LABEL = {
  commission_invoice: 'Commission billing',
  vat_invoice: 'VAT documentation',
  credit_note: 'Credit adjustment',
};
const statementsColumns = (t) => [
  { id: 'number', header: 'Statement Ref.',
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{r.statement_number || '—'}</span>,
    width: 'w-40' },
  { id: 'document_type', header: 'Kind',
    render: (r) => <Pill tone="violet">{STATEMENT_KIND_LABEL[r.document_type] || (r.document_type || '—').replace(/_/g, ' ')}</Pill> },
  { id: 'booking', header: 'Booking',
    render: (r) => r.booking_id
      ? <span className="font-mono text-[10.5px] text-slate-600">{r.booking_id.slice(0, 14)}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'milestone', header: 'Milestone', align: 'right',
    render: (r) => r.milestone_seq != null
      ? <span className="font-mono text-[11px] text-slate-600">#{r.milestone_seq}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'net', header: 'Net', align: 'right',
    render: (r) => <span className="text-slate-700">{fmtMoney(r.amount_net, r.currency)}</span> },
  { id: 'vat', header: 'VAT', align: 'right',
    render: (r) => r.vat_amount > 0
      ? <span className="text-amber-700">{fmtMoney(r.vat_amount, r.currency)}</span>
      : <span className="text-slate-300">—</span> },
  { id: 'gross', header: 'Gross', align: 'right',
    render: (r) => <span className="font-semibold text-slate-800">{fmtMoney(r.amount_gross, r.currency)}</span> },
  { id: 'status', header: 'Status',
    render: (r) => <Pill tone={STATUS_TONE[r.status] || 'slate'}>{_statusLabel(t, r.status)}</Pill> },
  { id: 'pdf', header: 'PDF',
    render: (r) => r.has_pdf
      ? <Pill tone="emerald">available</Pill>
      : <span className="text-slate-300">—</span> },
  { id: 'issued', header: 'Issued',
    render: (r) => <span className="text-slate-500">{fmtRel(r.issued_at || r.created_at)}</span> },
];

const ROW_KEY_FOR = {
  receivables:   (r) => r.order_id,
  payables:      (r) => r.record_id,
  commissions:   (r) => r.record_id,
  refunds:       (r) => r.refund_id,
  subscriptions: (r) => r.transaction_id,
  contracts:     (r) => r.contract_id,
  history:       (r) => `${r.date || ''}::${r.reference || ''}`,
  statements:    (r) => r.id,
};

/* -------------------------------------------------------------------- */
/*  Main component                                                      */
/* -------------------------------------------------------------------- */
export default function AccountantWorkspace() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { t } = useTranslation();
  // COMMISSION-STATEMENT-UI-01 · role-boundary-fix-02
  // Provider/clinic admins DO reach this workspace (existing broad
  // guard), but the Platform Commission Statements tab is scoped to
  // the finance/platform-admin authorities only. Server-side enforces
  // the same rule; this hides the tab to remove a broken affordance.
  const isPlatformAdmin = !!user && (
    user.role === 'treatx_super_admin' ||
    user.role === 'treatx_admin' ||
    (user.roles || []).some((r) => r === 'treatx_super_admin' || r === 'treatx_admin')
  );
  const isAccountant = !!user && (
    user.role === 'provider_accountant' ||
    (user.roles || []).includes('provider_accountant')
  );
  const canSeeStatements = isPlatformAdmin || isAccountant;
  const visibleViews = useMemo(
    () => VIEWS.filter((v) => v.id !== 'statements' || canSeeStatements),
    [canSeeStatements],
  );

  const [view, setView] = useState(() => {
    // O82 — allow an explicit entry view (e.g. ?view=finance from the provider's
    // iyzico panel). Allow-listed against VIEWS; visibility rules still apply.
    const requested = new URLSearchParams(window.location.hash.split('?')[1] || window.location.search).get('view');
    return VIEWS.some((v) => v.id === requested) ? requested : 'receivables';
  });
  const [bucket, setBucket] = useState('all');
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/clinic/accountant/summary');
      if (data?.success) setSummary(data);
    } catch { setSummary(null); }
  }, []);

  const fetchView = useCallback(async () => {
    // UI-B · Finance view is a self-contained composition — it fetches
    // canonical statements + booking-detail on its own; no legacy items.
    if (view === 'finance' || view === 'reports') {
      setLoading(false);
      setActiveRow(null);
      setItems([]);
      return;
    }
    setLoading(true);
    setActiveRow(null);
    setItems([]);
    try {
      const path = VIEWS.find((v) => v.id === view)?.path;
      const query = view === 'receivables' && bucket !== 'all' ? `?bucket=${bucket}` : '';
      const { data } = await api.get(`/clinic/accountant/${path}${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [view, bucket]);

  useEffect(() => {
    fetchSummary();
    fetchView();
  }, [fetchSummary, fetchView, reloadTick]);

  const columns = useMemo(() => {
    if (view === 'receivables')   return receivablesColumns(t);
    if (view === 'payables')      return payablesColumns(t);
    if (view === 'commissions')   return commissionsColumns(t);
    if (view === 'refunds')       return refundsColumns(t);
    if (view === 'subscriptions') return subscriptionsColumns(t);
    if (view === 'contracts')     return contractsColumns(t);
    if (view === 'statements')    return statementsColumns(t);
    return historyColumns(t);
  }, [view, t]);

  const chips = useMemo(() => {
    if (view !== 'receivables') return [];
    return BUCKETS.map((c) => {
      let count;
      if (c.id === 'all')              count = (summary?.buckets?.receivables_open ?? 0) + (summary?.buckets?.receivables_paid ?? 0);
      else if (c.id === 'outstanding') count = summary?.buckets?.receivables_open;
      else if (c.id === 'paid')        count = summary?.buckets?.receivables_paid;
      return { ...c, count: count ?? 0 };
    });
  }, [summary, view]);

  const rowKey = ROW_KEY_FOR[view];

  return (
    <div data-testid="accountant-workspace" className="min-h-[calc(100vh-4rem)] bg-slate-50 flex flex-col">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20" data-testid="acc-header">
        <div className="px-5 py-2 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Calculator className="h-4 w-4 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Accountant Workspace</div>
              <div className="text-[13px] font-semibold text-slate-800 truncate" data-testid="acc-clinic-name">
                Clinic · {summary?.clinic_id || '—'}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
            {summary?.buckets && (
              <>
                {summary.currency ? (
                  <span data-testid="acc-balance">Net: <strong className={summary.buckets.closing_balance >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{fmtMoney(summary.buckets.closing_balance, summary.currency)}</strong></span>
                ) : (
                  <span data-testid="acc-balance-mixed" className="text-amber-700">
                    {t('accountingScope.mixedCurrency.title')} {(summary.totals_by_currency || []).map((b) => b.currency).join(', ')}
                  </span>
                )}
                <span className="text-slate-300">·</span>
              </>
            )}
            <button
              type="button"
              onClick={() => setReloadTick((t) => t + 1)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 text-slate-700"
              data-testid="acc-refresh-btn"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {/* UI-A · legacy `/clinic/accountant/payouts` route + manual withdrawal-request workflow retired.
                Provider settlement is now fully automatic (F6C); the upcoming Finance UI (UI-B) will consume
                the canonical `GET /api/finance/provider/{clinic_id}/statement` composition. */}
            {/* ESCROW-UI-01: open escrow admin. Uses navigate() because the app is mounted under <HashRouter>. */}
            <button
              type="button"
              onClick={() => navigate('/clinic/accountant/escrow')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 text-slate-700"
              data-testid="acc-escrow-link"
            >
              Escrow
            </button>
          </div>
        </div>
        <nav className="px-5 border-t border-slate-100 flex items-center gap-0 overflow-x-auto" data-testid="acc-view-tabs">
          {visibleViews.map((v) => {
            const active = v.id === view;
            const count =
              v.id === 'receivables' ? (summary?.buckets?.receivables_open ?? 0) + (summary?.buckets?.receivables_paid ?? 0)
              : v.id === 'commissions' ? summary?.buckets?.commissions_total
              : v.id === 'refunds' ? summary?.buckets?.refunds_total
              : v.id === 'subscriptions' ? summary?.buckets?.subscriptions_total
              : v.id === 'contracts' ? summary?.buckets?.contracts_total
              : null;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                data-testid={`acc-tab-${v.id}`}
                className={`text-[12px] px-3 py-2 border-b-2 transition-colors -mb-px ${
                  active ? 'border-slate-900 text-slate-900 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {v.id === 'finance' ? t('provider.finance.tabLabel') : v.label}
                {count != null && (
                  <span className={`ml-1.5 text-[10px] ${active ? 'text-slate-600' : 'text-slate-400'}`}>{count}</span>
                )}
              </button>
            );
          })}
        </nav>
      </header>

      {/* FILTER CHIPS or summary strip */}
      {view === 'finance' ? null : view === 'receivables' ? (
        <FilterChips
          chips={chips}
          activeId={bucket}
          onChange={setBucket}
          testIdPrefix="acc-filter-chip"
          resultLabel={`${items.length} of ${(summary?.buckets?.receivables_open ?? 0) + (summary?.buckets?.receivables_paid ?? 0)} order(s)`}
        />
      ) : view === 'statements' ? (
        // COMMISSION-STATEMENT-UI-01: mandatory disclaimer strip so this
        // view is never confused with a statutory tax invoice / e-Fatura.
        <div
          className="px-5 py-2 border-b border-amber-200 bg-amber-50 flex items-center gap-2 text-[11px] text-amber-800"
          data-testid="commission-statements-disclaimer"
        >
          <strong className="font-semibold">Platform Commission Statement — NOT a statutory tax invoice / e-Fatura.</strong>
          <span className="text-amber-700">
            Commercial record of platform commission owed. Tax invoices must be issued through your accounting workflow.
          </span>
          <span className="ml-auto">{items.length} statement(s)</span>
        </div>
      ) : (
        <div className="px-5 py-2 border-b border-slate-100 bg-white flex items-center gap-2 text-[11px] text-slate-500">
          <span>{items.length} row(s)</span>
          {summary?.buckets && (
            <>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.commissions_total} commissions</span>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.refunds_open} open refund(s)</span>
              <span className="text-slate-300">·</span>
              <span>{summary.buckets.subscriptions_active} active sub(s)</span>
            </>
          )}
        </div>
      )}

      {/* P0-FIN-REPORT-SCOPE-01 — these grids are OPERATIONAL lifecycle
          records from the operational store, not posted ledger accounting.
          The Reports view is the posted PostgreSQL authority. */}
      {!['reports', 'statements'].includes(view) && (
        <div className="px-5 py-1.5 border-b border-sky-200 bg-sky-50 text-[11px] text-sky-900"
          data-testid="operational-basis-note">
          <span className="font-medium">{t('accountingBasis.operational.title')} </span>
          {t('accountingBasis.operational.body')}
        </div>
      )}

      {/* GRID + DRAWER */}
      {view === 'reports' ? (
        <div className="flex-1 min-h-0 overflow-auto p-4" data-testid="provider-accounting-reports">
          <AccountingGradeReports scopeLabel={t('accountingScope.reports.clinic')} />
        </div>
      ) : view === 'finance' ? (
        <ProviderFinance clinicId={summary?.clinic_id} />
      ) : (
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0">
          <DataGrid
            testId="acc"
            columns={columns}
            rows={items}
            rowKey={rowKey}
            activeRowId={activeRow ? rowKey(activeRow) : null}
            onRowClick={setActiveRow}
            loading={loading}
            loadingMessage="Loading workspace…"
            emptyMessage={
              view === 'receivables'   ? 'No orders in this bucket.'
              : view === 'payables'    ? 'No payable obligations.'
              : view === 'commissions' ? 'No commission records yet.'
              : view === 'refunds'     ? 'No refunds.'
              : view === 'subscriptions' ? 'No subscription transactions.'
              : view === 'contracts'   ? 'No financial contracts.'
              : view === 'statements'  ? 'No commission statements issued yet.'
              : 'No movements yet.'
            }
            selectable={false}
            maxHeightClass="h-[calc(100vh-220px)]"
          />
        </div>

        {activeRow && (
          <AcDrawer view={view} row={activeRow} onClose={() => setActiveRow(null)} />
        )}
      </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Drawer per view                                                     */
/* -------------------------------------------------------------------- */
function AcDrawer({ view, row, onClose }) {
  const title =
    view === 'receivables'   ? `Order ${(row.order_id || '').slice(-8)} · ${row.patient_name || '—'}`
    : view === 'payables'    ? `${row.obligation_type || 'payable'} · ${(row.record_id || '').slice(0, 12)}`
    : view === 'commissions' ? `Commission ${(row.record_id || '').slice(0, 12)}`
    : view === 'refunds'     ? `Refund ${(row.refund_id || '').slice(0, 12)}`
    : view === 'subscriptions' ? `Subscription · ${row.package_key || '—'}`
    : view === 'contracts'   ? `Contract ${(row.contract_id || '').slice(0, 14)}`
    : view === 'statements'  ? `Commission Statement ${row.statement_number || ''}`.trim()
    : `${row.movement_type || '—'} · ${row.reference || ''}`;

  const eyebrow =
    view === 'receivables'   ? `A/R · ${row.currency || 'EUR'}`
    : view === 'payables'    ? `A/P · ${row.currency || 'EUR'}`
    : view === 'commissions' ? `Commission · ${row.sale_class || '—'}`
    : view === 'refunds'     ? `Refund · ${row.refund_type || '—'}`
    : view === 'subscriptions' ? `Subscription · ${row.status || '—'}`
    : view === 'contracts'   ? `Contract · ${row.commission_type || '—'}`
    : view === 'statements'  ? `Platform commission · ${row.currency || 'EUR'}`
    : 'Statement entry';

  return (
    <DetailDrawer open onClose={onClose} title={title} eyebrow={eyebrow} testId="acc-drawer">
      {view === 'receivables' && (
        <>
          <DrawerField label="Order ID"      value={row.order_id || '—'} />
          <DrawerField label="Patient"       value={row.patient_name || '—'} />
          <DrawerField label="Country"       value={row.country || '—'} />
          <DrawerField label="Treatment"     value={row.treatment_name || '—'} />
          <DrawerField label="Booking"       value={row.booking_id || '—'} />
          <DrawerField label="Case"          value={row.case_id || '—'} />
          <DrawerField label="Quote"         value={row.quote_id || '—'} />
          <DrawerField label="Amount"        value={fmtMoney(row.amount, row.currency)} />
          <DrawerField label="Status"        value={row.status || '—'} />
          <DrawerField label="Overdue"       value={row.is_overdue ? 'Yes' : 'No'} />
          <DrawerField label="Created"       value={fmtRel(row.created_at)} />
          <DrawerField label="Due date"      value={fmtDate(row.due_date)} />
        </>
      )}
      {view === 'payables' && (
        <>
          <DrawerField label="Record ID"   value={row.record_id || '—'} />
          <DrawerField label="Payment ID"  value={row.payment_id || '—'} />
          <DrawerField label="Order ID"    value={row.order_id || '—'} />
          <DrawerField label="Obligation"  value={row.obligation_type || '—'} />
          <DrawerField label="Gross"       value={fmtMoney(row.gross, row.currency)} />
          <DrawerField label="Commission"  value={fmtMoney(row.commission, row.currency)} />
          <DrawerField label="Net"         value={fmtMoney(row.net_amount, row.currency)} />
          <DrawerField label="Reserve"     value={fmtMoney(row.reserve, row.currency)} />
          <DrawerField label="Payout state" value={row.payout_status || '—'} />
          <DrawerField label="State"        value={row.state || '—'} />
          <DrawerField label="Sale class"   value={row.sale_class || '—'} />
          <DrawerField label="Rate (bps)"   value={row.rate_bps_at_freeze ?? '—'} />
          {row.materialization_id && <DrawerField label="Materialization" value={row.materialization_id} />}
          <DrawerField label="Created"      value={fmtRel(row.created_at)} />
        </>
      )}
      {view === 'commissions' && (
        <>
          <DrawerField label="Record ID"        value={row.record_id || '—'} />
          <DrawerField label="Payment ID"       value={row.payment_id || '—'} />
          <DrawerField label="Order ID"         value={row.order_id || '—'} />
          <DrawerField label="Sale class"       value={row.sale_class || '—'} />
          <DrawerField label="Gross"            value={fmtMoney(row.gross, row.currency)} />
          <DrawerField label="Commission"       value={fmtMoney(row.commission, row.currency)} />
          <DrawerField label="Net to provider"  value={fmtMoney(row.net, row.currency)} />
          <DrawerField label="Rate at freeze"   value={row.rate_bps_at_freeze != null ? `${(row.rate_bps_at_freeze/100).toFixed(2)}%` : '—'} />
          <DrawerField label="Contract version" value={row.contract_version_id || '—'} />
          <DrawerField label="Materialization"  value={row.materialization_id || '—'} />
          <DrawerField label="Fingerprint"      value={row.fingerprint || '—'} />
          <DrawerField label="State"            value={row.state || '—'} />
          <DrawerField label="Frozen at"        value={fmtRel(row.created_at)} />
          {Array.isArray(row.lineage_chain) && row.lineage_chain.length > 0 && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Lineage chain</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">{JSON.stringify(row.lineage_chain, null, 2)}</pre>
            </div>
          )}
        </>
      )}
      {view === 'refunds' && (
        <>
          <DrawerField label="Refund ID"   value={row.refund_id || '—'} />
          <DrawerField label="Payment ID"  value={row.payment_id || '—'} />
          <DrawerField label="Order ID"    value={row.order_id || '—'} />
          <DrawerField label="Amount"      value={fmtMoney(row.amount, row.currency)} />
          <DrawerField label="Type"        value={row.refund_type || '—'} />
          <DrawerField label="Reason code" value={row.reason_code || '—'} />
          <DrawerField label="Reason"      value={row.reason || '—'} />
          <DrawerField label="Status"      value={row.status || '—'} />
          <DrawerField label="Requested by" value={row.requested_by || '—'} />
          <DrawerField label="Approved by"  value={row.approved_by || '—'} />
          <DrawerField label="Created"     value={fmtRel(row.created_at)} />
          <DrawerField label="Completed"   value={row.completed_at ? fmtRel(row.completed_at) : '—'} />
        </>
      )}
      {view === 'subscriptions' && (
        <>
          <DrawerField label="Txn"      value={row.transaction_id || '—'} />
          <DrawerField label="Plan"     value={row.package_key || '—'} />
          <DrawerField label="Amount"   value={fmtMoney(row.amount, (row.currency || 'EUR').toUpperCase())} />
          <DrawerField label="Trial"    value={row.trial ? 'Yes' : 'No'} />
          <DrawerField label="Status"   value={row.status || '—'} />
          <DrawerField label="Started"  value={fmtRel(row.started_at)} />
          <DrawerField label="Renewal"  value={row.renewal_at ? fmtDate(row.renewal_at) : '—'} />
          <DrawerField label="Ends"     value={row.ends_at ? fmtDate(row.ends_at) : '—'} />
          {row.limits && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Limits</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">{JSON.stringify(row.limits, null, 2)}</pre>
            </div>
          )}
        </>
      )}
      {view === 'contracts' && (
        <>
          <DrawerField label="Contract ID"      value={row.contract_id || '—'} />
          <DrawerField label="Offer ID"         value={row.offer_id || '—'} />
          <DrawerField label="Patient"          value={row.patient_id || '—'} />
          <DrawerField label="Commission type"  value={row.commission_type || '—'} />
          <DrawerField label="Commission value" value={row.commission_value ?? '—'} />
          <DrawerField label="Rate (bps)"       value={row.effective_rate_bps ?? '—'} />
          <DrawerField label="Commission amount" value={fmtMoney(row.commission_amount, row.currency)} />
          <DrawerField label="Platform fee"     value={fmtMoney(row.platform_fee, row.currency)} />
          <DrawerField label="Net to provider"  value={fmtMoney(row.net_transfer_to_provider, row.currency)} />
          <DrawerField label="Country"          value={row.country || '—'} />
          <DrawerField label="Campaign"         value={row.campaign_id || '—'} />
          <DrawerField label="Resolved by rule" value={row.resolved_by_rule || '—'} />
          <DrawerField label="Status"           value={row.status || '—'} />
          <DrawerField label="Effective"        value={fmtRel(row.effective_at)} />
        </>
      )}
      {view === 'history' && (
        <>
          <DrawerField label="Date"            value={fmtDate(row.date || row.created_at)} />
          <DrawerField label="Movement"        value={row.movement_type || '—'} />
          <DrawerField label="Reference"       value={row.reference || '—'} />
          <DrawerField label="Counterparty"    value={`${row.counterparty_type || '—'}${row.counterparty_id ? ' · ' + row.counterparty_id.slice(0,12) : ''}`} />
          <DrawerField label="Gross"           value={fmtMoney(row.gross_amount, row.currency)} />
          <DrawerField label="Debit"           value={fmtMoney(row.debit, row.currency)} />
          <DrawerField label="Credit"          value={fmtMoney(row.credit, row.currency)} />
          <DrawerField label="Status"          value={row.status || '—'} />
          <DrawerField label="Source"          value={row.source || '—'} />
          {row.notes && <DrawerField label="Notes" value={row.notes} />}
          {row.related_order_id && <DrawerField label="Related order" value={row.related_order_id} />}
        </>
      )}
      {view === 'statements' && (
        <>
          {/* COMMISSION-STATEMENT-UI-01: keep the disclaimer inside the drawer
              so a screenshot / share of the detail view still carries the
              non-statutory framing. */}
          <div
            className="mb-2 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800"
            data-testid="commission-statement-drawer-disclaimer"
          >
            <strong>Platform Commission Statement</strong> — NOT a statutory tax invoice / e-Fatura.
          </div>
          <DrawerField label="Statement reference" value={row.statement_number || '—'} />
          <DrawerField label="Kind"                 value={STATEMENT_KIND_LABEL[row.document_type] || row.document_type || '—'} />
          <DrawerField label="Booking"          value={row.booking_id || '—'} />
          <DrawerField label="Milestone seq"    value={row.milestone_seq != null ? `#${row.milestone_seq}` : '—'} />
          <DrawerField label="Net"              value={fmtMoney(row.amount_net, row.currency)} />
          <DrawerField label="VAT"              value={fmtMoney(row.vat_amount, row.currency)} />
          <DrawerField label="Gross"            value={fmtMoney(row.amount_gross, row.currency)} />
          <DrawerField label="Status"           value={row.status || '—'} />
          <DrawerField label="Issued"           value={fmtRel(row.issued_at)} />
          <DrawerField label="Created"          value={fmtRel(row.created_at)} />
          {row.has_pdf && row.id && (
            <div className="pt-2">
              <a
                href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/admin/invoices/${row.id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-slate-700 hover:text-slate-900 underline"
                data-testid="commission-statement-download"
              >
                <ExternalLink className="h-3 w-3" /> Download Commission Statement PDF
              </a>
            </div>
          )}
        </>
      )}
    </DetailDrawer>
  );
}

const fmtMoney2 = fmtMoney; // ensure tree-shake doesn't drop helper used in subcomponent
void fmtMoney2; void ExternalLink;
